import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { useApi } from "@/lib/api";
import { Section } from "@/components/intel/Section";
import { ItemCardEmpty } from "@/components/intel/ItemCard";
import { JoinButton } from "@/components/circles/JoinButton";
import { MembersPreview } from "@/components/circles/MembersPreview";
import { GuidedPrompt } from "@/components/circles/GuidedPrompt";
import { PinnedResources } from "@/components/circles/PinnedResources";
import { PromptArchive } from "@/components/circles/PromptArchive";

interface CircleDetailPayload {
  circle: {
    id: string;
    slug: string;
    name: string;
    kind: string;
    description: string | null;
    cadence: string | null;
    is_private: number;
    status: string;
    created_at: number;
  };
  membership: {
    role: "member" | "leader" | "moderator";
    status: "active" | "pending";
  } | null;
  member_count: number;
  members_preview: Array<{
    id: string;
    display_name: string | null;
    avatar_url: string | null;
    role: "member" | "leader" | "moderator";
    joined_at: number;
  }>;
  prompt: {
    id: string;
    category: string;
    body: string;
    ai_suggested: number;
    created_at: number;
  } | null;
  resources: Array<{
    id: string;
    title: string;
    url: string | null;
    note: string | null;
    pinned_at: number;
  }>;
}

interface SummaryPayload {
  active_participation: number;
  recurring_theme: { category: string; count: number } | null;
}

export function CircleDetail() {
  const { slug = "" } = useParams();
  const api = useApi();

  const detail = useQuery({
    queryKey: ["circle", slug],
    queryFn: () => api.get<CircleDetailPayload>(`/circles/${encodeURIComponent(slug)}`),
    enabled: slug.length > 0,
  });

  const isActiveMember =
    detail.data?.membership?.status === "active";
  const circleId = detail.data?.circle.id;

  // Summary endpoint requires active membership — gate the query.
  const summary = useQuery({
    queryKey: ["circle-summary", circleId ?? ""],
    queryFn: () =>
      api.get<SummaryPayload>(`/circles/${circleId!}/summary`),
    enabled: !!circleId && isActiveMember,
    staleTime: 60_000,
  });

  if (detail.isLoading) {
    return (
      <div className="px-10 py-10 max-w-5xl">
        <ItemCardEmpty label="Reading circle…" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="px-10 py-10 max-w-5xl">
        <Link
          to="/app/circles"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
        >
          <ArrowLeft className="size-3" strokeWidth={1.5} />
          Back to circles
        </Link>
        <div className="mt-8">
          <ItemCardEmpty
            label="Couldn't load this circle."
            detail="It may have been archived or you don't have access."
          />
        </div>
      </div>
    );
  }

  const { circle, membership, member_count, members_preview, prompt, resources } = detail.data;
  const canLead =
    membership?.status === "active" &&
    (membership.role === "leader" || membership.role === "moderator");
  const recurring = summary.data?.recurring_theme ?? null;

  return (
    <div className="px-10 py-10 max-w-5xl">
      <Link
        to="/app/circles"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
      >
        <ArrowLeft className="size-3" strokeWidth={1.5} />
        Back to circles
      </Link>

      <header className="mt-6 mb-10">
        <p className="label-eyebrow">
          {circle.kind}
          {circle.cadence && <span className="text-muted"> · {circle.cadence}</span>}
          {circle.is_private === 1 && (
            <span className="ml-2 inline-flex items-center gap-1 text-muted">
              <Lock className="size-3" strokeWidth={1.5} />
              private
            </span>
          )}
        </p>
        <h1 className="mt-2 text-display-2 font-display">{circle.name}</h1>
        {circle.description && (
          <p className="mt-3 text-whisper max-w-2xl leading-relaxed">
            {circle.description}
          </p>
        )}
        {recurring && (
          <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
            Recurring theme · {recurring.category} ({recurring.count} prompts)
          </p>
        )}
        <div className="mt-6 flex items-center justify-between gap-6">
          <MembersPreview members={members_preview} totalCount={member_count} />
          <JoinButton
            circleId={circle.id}
            circleSlug={circle.slug}
            isPrivate={circle.is_private === 1}
            membership={membership}
          />
        </div>
      </header>

      <Section eyebrow="This week" title="Guided prompt">
        <GuidedPrompt
          circleId={circle.id}
          prompt={prompt}
          canEdit={canLead}
          isMember={isActiveMember}
          participationCount={summary.data?.active_participation}
        />
      </Section>

      <Section eyebrow="Library" title="Pinned resources">
        <PinnedResources resources={resources} canPin={canLead} />
      </Section>

      {isActiveMember && (
        <Section eyebrow="History" title="Prompt archive">
          <PromptArchive circleId={circle.id} />
        </Section>
      )}
    </div>
  );
}
